import { getUMKFromLocal } from 'pUtils'

const DB_NAME = 'safebox_attachment_cache'
const STORE_NAME = 'attachments'
const DB_VERSION = 1
const CACHE_TTL_MS = 60 * 60 * 1000

async function deriveKey() {
  const umk = await getUMKFromLocal()
  if (!umk) throw new Error('UMK not available')
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(umk),
    { name: 'HKDF' },
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode('safebox-attachment-cache'),
      info: new Uint8Array(),
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function encryptBuffer(buffer) {
  const key = await deriveKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, buffer)
  const result = new Uint8Array(12 + encrypted.byteLength)
  result.set(iv, 0)
  result.set(new Uint8Array(encrypted), 12)
  return result.buffer
}

async function decryptBuffer(buffer) {
  const key = await deriveKey()
  const data = new Uint8Array(buffer)
  const iv = data.slice(0, 12)
  const encrypted = data.slice(12)
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted)
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' })
      }
    }
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = () => reject(new Error('Failed to open attachment cache DB'))
  })
}

export async function getCachedFile(cacheKey) {
  try {
    const db = await openDB()
    const entry = await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(cacheKey)
      req.onsuccess = (e) => resolve(e.target.result)
      req.onerror = () => resolve(null)
    })
    if (!entry || Date.now() - entry.timestamp > CACHE_TTL_MS) return null
    const decrypted = await decryptBuffer(entry.buffer)
    return new Blob([decrypted], { type: entry.mimeType })
  } catch {
    return null
  }
}

export async function setCachedFile(cacheKey, blob) {
  try {
    const buffer = await blob.arrayBuffer()
    const encrypted = await encryptBuffer(buffer)
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put({
        cacheKey,
        buffer: encrypted,
        mimeType: blob.type,
        timestamp: Date.now(),
      })
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    })
  } catch {
    return undefined
  }
}

export async function clearAttachmentCache() {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).clear()
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    })
  } catch {
    return undefined
  }
}
